import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { signAsset } from '@/api/assets';
import { postApplicationAsset } from '@/api/Rover/ApplicationAssets';
import type { IGetApplicationAssetsServiceResData, IPostApplicationAssetPayload } from '@/api/Rover/ApplicationAssets/types';
import UploadButton from '@/components/UI/Button/UploadButton';
import Card from '@/components/UI/Card/index.tsx';
import PlusIcon from '@/components/UI/Icons/PlusIcon';
import LoadingDocumentLibrary from '@/components/UI/loading/LoadingLibraryItem';
import { useApplicationAssets } from '@/hooks/ReactQueryHooks/useApplicationQuery';
import { useFileUpload } from '@/hooks/useFileUpload';
import type { ApplicationPhaseType } from '@/types/ClientApplication';
import { RoverReactQueryKey } from '../../../../../constants/ReactQueryKeys';
import { notifyUser } from '../../../../../store/Toast';
import { getAcceptedFileTypes, sanitizeResponseError } from '../../../../../utils';
import DueDiligenceDocumentItem from './DueDiligenceDocumentItems';

const ApplicationAssets = ({
  title,
  phase,
}: {
  title: string;
  phase: {
    label: string;
    value: ApplicationPhaseType;
  };
}) => {
  const { applicationId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { upload } = useFileUpload({ signAsset: signAsset });

  const fetchApplicationAssets = useApplicationAssets({
    applicationId: applicationId!,
  });
  const applicationAssets = fetchApplicationAssets?.data?.data;

  const addApplicationAsset = useMutation(
    (data: { assets: IPostApplicationAssetPayload[] }) => postApplicationAsset({ applicationId: applicationId!, data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_ASSETS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'File uploaded successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const updateLibrary = useMutation(
    (payload: {
      signAssetPayload: {
        key: string;
        type: string;
        resource: string;
      }[];
      files: File[];
    }) => upload(payload),
    {
      onSuccess: (response) => {
        const assetsFiles = response?.files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: response?.sign.fKey ?? response.sign[index].fKey,
            contentType: file.type,
            size: file.size,
            assetType: 'file',
          };
        });

        addApplicationAsset.mutate({ assets: assetsFiles });
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const fileUploadHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      const files = [...e.target.files];

      files.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          const fileExists = applicationAssets.find((asset: any) => asset?.name === file?.name);

          if (fileExists) {
            dispatch(
              notifyUser({
                showToast: true,
                toastMessage: `A file with the name ${file.name} already exists in this phase.`,
                toastType: 'failed',
              })
            );
          } else {
            validFiles.push(file);
          }
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }

      if (validFiles.length > 0) {
        const signPayload = validFiles.map<{
          key: string;
          type: string;
          resource: string;
        }>((file) => ({
          key: file.name,
          type: file.type,
          resource: 'applications',
        }));

        updateLibrary.mutate({
          signAssetPayload: signPayload,
          files: validFiles,
        });
      }
    }
  };

  const titleElements = (
    <div className='flex items-center'>
      <UploadButton
        icon={<PlusIcon />}
        name={`${phase.label}DocumentsUpload`}
        fileTypesAccepted={getAcceptedFileTypes()}
        multipleFiles={true}
        customStyle={'text-primaryColor-normal hover:text-primaryColor-dark'}
        changeHandler={fileUploadHandler}
      />
    </div>
  );
  return (
    <Card title='Documents' titleElement={titleElements}>
      {/* document lists    */}
      <div className='px-6 mt-2 h-80 overflow-y-scroll'>
        {fetchApplicationAssets.isLoading || (addApplicationAsset.isLoading && <LoadingDocumentLibrary />)}
        {applicationAssets?.length === 0 && (
          <div className='flex flex-col justify-center items-center mt-20 gap-4'>
            <span>There are no files uploaded for this due diligence yet.</span>
            <div className='flex items-center justify-center bg-primaryColor-normal rounded-full text-white cursor-pointer hover:bg-primaryColor-medium transition duration-300 ease-in-out'>
              <UploadButton
                icon={<CloudArrowUpIcon className='h-5 w-5' />}
                name={`${phase.label}DocumentsUpload`}
                fileTypesAccepted={getAcceptedFileTypes()}
                multipleFiles={true}
                customStyle={'w-full'}
                changeHandler={fileUploadHandler}
                label='Upload'
              />
            </div>
          </div>
        )}
        {applicationAssets?.length !== 0 &&
          applicationAssets?.map((document: IGetApplicationAssetsServiceResData) => (
            <DueDiligenceDocumentItem key={document.id} file={document} />
          ))}
      </div>
    </Card>
  );
};

interface DocumentProps {
  title: string;
  phase: {
    label: string;
    value: ApplicationPhaseType;
  };
}

const DueDiligenceDocument = (props: DocumentProps) => {
  return <ApplicationAssets {...props} />;
};
export default DueDiligenceDocument;
